import React, {useCallback, useMemo, useState} from 'react';
import {Page, StorePageCount} from "../../components/page/Page";
import {LoadingData} from "../../components/loading-data/LoadingData";
import {Box, Button, Stack, Tooltip} from "@mui/material";
import {
    filterNeighbourhoodVisits,
    NeighbourhoodListFilter,
    neighbourhoodListFilterSerializer
} from "./NeighbourhoodListFilter";
import {
    NeighbourhoodCard,
    NeighbourhoodPaneEmployeeBlock,
    NeighbourhoodPaneStatBlock,
    NeighbourhoodPaneVisitChartBlock
} from "../../components/neighbourhood-pane/NeighbourhoodCard";
import PsychologyIcon from "@mui/icons-material/Psychology";
import EnvoySyncButton from "./EnvoySyncButton";
import {GuessNeighbourhoodDialog} from "../../components/guess-neighbourhood-dialog/GuessNeighbourhoodDialog";
import {useFormDialogAction} from "../../components/form-dialog/FormDialogProvider";
import {isAssigned, Neighbourhood} from "../../api/Model";
import {NeighbourhoodFormComponent} from "./NeighbourhoodFormComponent";
import {useNeighbourhoodStore} from "../../api/NeighbourhoodStore";
import {useFilter} from "../../util/FilterSerializer";
import {useEmployeeVisitStore} from "../../api/EmployeeVisitStore";
import {RemoteValue} from "../../util/RemoteValue";
import {useAssignedDeskStore} from "../../api/AssignedDeskStore";
import DownloadIcon from "@mui/icons-material/Download";
import {useMessage} from "../../util/actions/Message";
import {downloadNeighbourhoodVisitCsv} from "./ExportNeighbourhoodVisitCsv";
import {Employees_Read, Neighbourhoods_Admin} from "../../util/access/Permission";
import {usePermission} from "../../util/access/UseAccess";
import {Virtuoso} from "react-virtuoso";
import {VisitDataCoverageWarning} from "../employee-visit-report/VisitDataCoverageWarning";
import {applyFilter, filterDateRange} from "../../util/FilterUtil";

export default function NeighbourhoodListPage() {
    const employeeVisits = useEmployeeVisitStore(state => state.remoteValue);
    const assignedDesks = useAssignedDeskStore(state => state.remoteValue);
    const {canAdmin, canSync, canGuess} = usePermission({
        canAdmin: Neighbourhoods_Admin,
        canSync: Neighbourhoods_Admin,
        canGuess: Employees_Read,
    });

    const {
        remoteValue: unfilteredRows,
        updateNeighbourhood,
    } = useNeighbourhoodStore();
    const [filter, setFilter] = useFilter(neighbourhoodListFilterSerializer);
    const [guessDialogOpen, setGuessDialogOpen] = useState(false);
    const message = useMessage();

    const handleOpenGuessDialog = () => {
        setGuessDialogOpen(true);
    };

    const handleCloseGuessDialog = () => {
        setGuessDialogOpen(false);
    };

    const handleEdit = useFormDialogAction({
        title: 'Edit Neighbourhood',
        description: 'Update the neighbourhood information below.',
        submitButtonText: 'Update',
        renderForm: NeighbourhoodFormComponent,
        action: (openDialog, neighbourhood: Neighbourhood) => openDialog({
            initialValue: {
                name: neighbourhood.name,
                displayColor: neighbourhood.displayColor || '',
                notes: neighbourhood.notes || '',
                slackChannelId: neighbourhood.slackChannelId || '',
            },
            onSubmit: (form) => updateNeighbourhood(neighbourhood, form),
            successMessage: form => `Updated neighbourhood ${form.name}.`,
            errorMessage: 'Failed to update neighbourhood. Please try again later.',
        }),
    }, [updateNeighbourhood]);


    const filtered = useMemo(() => {
        return RemoteValue
            .combine({unfilteredRows, employeeVisits, assignedDesks})
            .map(({unfilteredRows, employeeVisits, assignedDesks}) => {
                const {filteredRows, statRanges} = filterNeighbourhoodVisits(employeeVisits, assignedDesks, unfilteredRows, filter);
                return ({
                    filteredRows,
                    statRanges,
                    length: filteredRows.length,
                    assignedDesks,
                    visitDates: applyFilter(employeeVisits.dates, filterDateRange(filter.dateRange)),
                });
            });
    }, [filter, unfilteredRows, employeeVisits, assignedDesks]);

    const handleExportCsv = useCallback(async () => {
        const {filteredRows: rows} = await filtered.value;
        if (rows.length <= 0) {
            message.error('No neighbourhoods found to export.');
        } else if (rows.every(row => row.visits.length <= 0)) {
            message.error('No visits found for the selected dates.');
        } else {
            downloadNeighbourhoodVisitCsv(rows);
        }
    }, [filtered, message]);

    return (
        <Page
            header="Neighbourhoods"
            counter={<StorePageCount totalValue={unfilteredRows} value={filtered}/>}
            actions={
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<DownloadIcon/>}
                        onClick={handleExportCsv}
                    >
                        Export CSV
                    </Button>
                    {canGuess && (
                        <Tooltip title="Guess which neighbourhood an employee may belong to">
                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<PsychologyIcon/>}
                                onClick={handleOpenGuessDialog}
                            >
                                Guess
                            </Button>
                        </Tooltip>
                    )}
                    {canSync && <EnvoySyncButton/>}
                </Stack>
            }
            filter={<NeighbourhoodListFilter filter={filter} setFilter={setFilter}/>}
        >
            <LoadingData
                totalValue={unfilteredRows}
                value={filtered}
                emptyMessage="No neighbourhoods found."
                noMatchMessage="No neighbourhoods match the current filters. Try adjusting your filter criteria."
                defaultErrorMessage="Failed to load neighbourhood visits data. Please try again later."
            >{({filteredRows: rows, assignedDesks, statRanges, visitDates}) => (
                <>
                <VisitDataCoverageWarning dates={visitDates} dateRange={filter.dateRange}/>
                <Virtuoso
                    data={rows}
                    useWindowScroll
                    computeItemKey={(_, row) => row.neighbourhood.id}
                    itemContent={(_, row) => (
                        <Box pb={2}>
                            <NeighbourhoodCard
                                neighbourhood={row.neighbourhood}
                                onEdit={canAdmin && isAssigned(row.neighbourhood) ? handleEdit : undefined}
                            >
                                <NeighbourhoodPaneEmployeeBlock stats={row} assignedDesks={assignedDesks} desks={row.neighbourhood.desks}/>
                                <NeighbourhoodPaneStatBlock
                                    deskCount={row.neighbourhood.desks.length}
                                    assignedDesks={assignedDesks}
                                    stats={row}
                                    statRanges={statRanges}
                                />
                                <NeighbourhoodPaneVisitChartBlock
                                    stats={row}
                                    deskCount={row.neighbourhood.desks.length}
                                />
                            </NeighbourhoodCard>
                        </Box>
                    )}
                />
                </>
            )}</LoadingData>
            <GuessNeighbourhoodDialog
                open={guessDialogOpen}
                onClose={handleCloseGuessDialog}
            />
        </Page>
    );
}
